import React, { useEffect, useRef, useState } from "react";
import { Play, RotateCcw } from "lucide-react";
import Reveal from "./Reveal";
import { DEMO_SCENARIOS } from "../mock";

export default function Demo() {
  const [active, setActive] = useState(0);
  const [running, setRunning] = useState(false);
  const [printed, setPrinted] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const timers = useRef([]);
  const scroller = useRef(null);

  const scenario = DEMO_SCENARIOS[active];

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  useEffect(() => () => clearTimers(), []);

  const reset = () => {
    clearTimers();
    setRunning(false);
    setPrinted([]);
    setShowResults(false);
  };

  const pick = (i) => {
    setActive(i);
    reset();
  };

  const run = () => {
    clearTimers();
    setPrinted([]);
    setShowResults(false);
    setRunning(true);
    scenario.lines.forEach((line, idx) => {
      const t = setTimeout(() => {
        setPrinted((p) => [...p, line]);
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
      }, 550 * (idx + 1));
      timers.current.push(t);
    });
    const done = setTimeout(() => {
      setRunning(false);
      setShowResults(true);
    }, 550 * (scenario.lines.length + 1));
    timers.current.push(done);
  };

  return (
    <section id="demo" style={{ padding: "72px 0", background: "var(--paper-2)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-x">
        <Reveal>
          <div className="section-label">Live interactive demo</div>
          <h2 className="h-section" style={{ marginTop: 18, maxWidth: 760 }}>
            Watch a campaign build. <span className="serif" style={{ fontSize: "1.05em" }}>Pick a market.</span>
          </h2>
          <p style={{ marginTop: 20, fontSize: 17, lineHeight: 1.6, color: "var(--ink-soft)", maxWidth: 560 }}>
            The same pipeline we run for clients: signals, verification, copy, sending. Pick a motion and run it.
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="flex flex-wrap gap-2" style={{ marginTop: 30 }}>
            {DEMO_SCENARIOS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => pick(i)}
                className="btn"
                style={{
                  background: i === active ? "var(--ink)" : "transparent",
                  color: i === active ? "var(--paper)" : "var(--ink)",
                  border: `1px solid ${i === active ? "var(--ink)" : "var(--line-strong)"}`,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={160}>
          <div className="grid lg:grid-cols-5 gap-5" style={{ marginTop: 22 }}>
            {/* Terminal */}
            <div className="lg:col-span-3" style={{ background: "#1c1a22", borderRadius: 8, border: "1px solid #322f3a", overflow: "hidden" }}>
              <div className="flex items-center justify-between" style={{ padding: "12px 16px", borderBottom: "1px solid #322f3a" }}>
                <div className="flex items-center gap-2">
                  {["#c9765a", "#c7a24a", "#7aa17f"].map((c) => (
                    <span key={c} style={{ width: 11, height: 11, borderRadius: 99, background: c, display: "inline-block" }} />
                  ))}
                  <span className="mono" style={{ color: "#8b8779", fontSize: 12, marginLeft: 8 }}>conduit / build</span>
                </div>
                <button onClick={run} disabled={running} className="flex items-center gap-1.5" style={{ fontSize: 12.5, color: running ? "#8b8779" : "#ece5d7", cursor: running ? "default" : "pointer" }}>
                  <Play size={13} /> Run the build
                </button>
              </div>
              <div ref={scroller} className="mono" style={{ padding: "18px 18px", height: 300, overflowY: "auto", fontSize: 13, lineHeight: 1.85, color: "#cfc9ba" }}>
                {printed.length === 0 && !running && (
                  <div style={{ color: "#8b8779" }}>$ awaiting build. Pick a market and hit run</div>
                )}
                {printed.map((l, i) => (
                  <div key={i} style={{ color: l.startsWith("$") ? "#ece5d7" : "#a9c3a5" }}>{l}</div>
                ))}
                {running && <span className="caret" style={{ color: "#a9c3a5" }}>█</span>}
                {!running && printed.length === 0 && <span className="caret" style={{ color: "#8b8779" }}>█</span>}
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-2 card-line" style={{ borderRadius: 6, padding: "22px 22px", display: "flex", flexDirection: "column" }}>
              <div className="flex items-center justify-between">
                <span className="eyebrow">Results</span>
                {showResults && (
                  <button onClick={reset} className="flex items-center gap-1.5" style={{ fontSize: 12, color: "var(--ink-soft)" }}>
                    <RotateCcw size={12} /> Reset
                  </button>
                )}
              </div>
              {!showResults ? (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-faint)", fontSize: 14, textAlign: "center", minHeight: 200 }}>
                  <span>// replies booked, not emails sent</span>
                </div>
              ) : (
                <div style={{ marginTop: 8 }}>
                  {scenario.results.map((r, i) => (
                    <div key={i} className="reveal in" style={{ animationDelay: `${i * 90}ms`, display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "14px 0", borderBottom: i < scenario.results.length - 1 ? "1px solid var(--line)" : "none" }}>
                      <span style={{ fontSize: 14, color: "var(--ink-soft)" }}>{r.k}</span>
                      <span className="serif" style={{ fontSize: 32, color: "var(--warm)" }}>{r.v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Reveal>

        <p style={{ marginTop: 18, fontSize: 13, color: "var(--ink-faint)" }}>
          Simulated run with illustrative numbers. Real volumes depend on your market and are scoped on the call.
        </p>
      </div>
    </section>
  );
}
